import { BarSeriesOption } from 'echarts'
import { defaultExportedChart, defineChart } from '..'
import { tooltip } from '@/composables/useChart/theme'

export default defineChart<
    [Import | undefined, (value: number) => string, string]
>((data, formatter, title) => {
    if (!data) return

    /**
     * Use metadata.
     */
    const { findProduct } = useMeta()

    /**
     * Use countries.
     */
    const { find } = useCountries()

    /**
     * Use params.
     */
    const { config } = useParams()
    /**
     * Filter products if needed.
     */
    const importedProducts = config.importProducts?.length
        ? data?.products.filter(({ product_id }) => {
              return config.importProducts?.includes(product_id)
          })
        : data?.products

    /**
     * Aggregated by country.
     */
    const aggregatedByCountry = importedProducts?.slice().reduce((acc, cur) => {
        const { origin_country, import_value } = cur

        if (!acc[origin_country]) {
            acc[origin_country] = 0
        }

        acc[origin_country] += import_value

        return acc
    }, {} as Record<string, number>)

    /**
     * Aggregated by product.
     */
    const aggregatedByProduct = importedProducts?.slice().reduce((acc, cur) => {
        const { product_id, import_value } = cur

        if (!acc[product_id]) {
            acc[product_id] = 0
        }

        acc[product_id] += import_value

        return acc
    }, {} as Record<number, number>)

    /**
     * List of products.
     */
    const products = Object.entries(aggregatedByProduct)
        .sort((a, b) => a[1] - b[1])
        .map(([id]) => findProduct(Number(id))) as Product[]

    /**
     * Origin countries sorted by import value.
     */
    const countries = Object.entries(aggregatedByCountry)
        .sort((a, b) => a[1] - b[1])
        .map(([iso]) => find(iso)) as Country[]

    /**
     * Start x position.
     */
    const spaceLeft = 100

    /**
     * End X position.
     */
    const spaceRight = 50

    /**
     * Create a series
     */
    const baseSeries = {
        type: 'bar',
        stack: 'all',
        barMinHeight: 5,
        labelLayout(params) {
            if (params.labelRect.width > params.rect.width) {
                if (
                    params.labelRect.height <= params.rect.width * 0.85 &&
                    params.labelRect.width <= params.rect.height * 0.85
                ) {
                    return {
                        rotate: 90
                    }
                }

                return {
                    fontSize: 0
                }
            }
        },
        label: {
            show: true,
            color: 'white'
        },
        tooltip: {
            formatter(params) {
                return tooltip('', params.name, formatter(Number(params.value)))
            }
        }
    } as BarSeriesOption

    return {
        grid: {
            top: '1%',
            left: spaceLeft,
            right: spaceRight,
            bottom: '1%',
            containLabel: false
        },
        tooltip: {},
        xAxis: [
            {
                show: false,
                type: 'value'
            },
            {
                type: 'value'
            }
        ],
        yAxis: [
            {
                id: 'left',
                type: 'category',
                offset: spaceLeft,
                data: config.importsByProduct
                    ? products.map(({ name }) => name)
                    : countries.map(({ name }) => name),
                axisLabel: {
                    interval: 0,
                    width: spaceLeft,
                    overflow: 'break',
                    align: 'left',
                    margin: 0
                },
                axisLine: {
                    show: false
                },
                axisTick: {
                    show: false
                }
            },
            {
                id: 'right',
                type: 'category',
                position: 'right',
                data: config.importsByProduct
                    ? products.map(({ id }) => aggregatedByProduct[id])
                    : countries.map(({ iso }) => aggregatedByCountry[iso]),
                axisLabel: {
                    interval: 0,
                    width: spaceRight,
                    overflow: 'break',
                    align: 'right',
                    margin: spaceRight,
                    formatter: formatter,
                    fontWeight: 'bold',
                    fontSize: 15
                },
                axisLine: {
                    show: false
                },
                axisTick: {
                    show: false
                }
            }
        ],
        series: config.importsByProduct
            ? seriesByProduct(products, countries, importedProducts, baseSeries)
            : seriesByCountry(
                  products,
                  countries,
                  importedProducts,
                  baseSeries
              ),

        export: defaultExportedChart(
            {
                title: {
                    text: title
                },
                grid: {
                    top: '10%',
                    bottom: '20%',
                    left: spaceLeft * 1.5,
                    right: spaceRight * 1.5
                }
            },
            importedProducts.map(({ product_id }) => {
                const { name, id, color } = findProduct(product_id) || {}
                return {
                    label: name,
                    value: id,
                    color
                }
            })
        ),
        media: [
            {
                query: {
                    maxWidth: 400
                },
                option: {
                    grid: {
                        left: 0,
                        right: 0
                    },
                    yAxis: [
                        {
                            id: 'left',
                            offset: 0,
                            axisLabel: {
                                show: false
                            }
                        },
                        {
                            id: 'right',
                            axisLabel: {
                                show: false
                            }
                        }
                    ],
                    series: {
                        barCategoryGap: '5%'
                    }
                }
            },
            {
                query: {
                    minWidth: 400
                },
                option: {
                    grid: {
                        left: spaceLeft,
                        right: spaceRight
                    },
                    yAxis: [
                        {
                            id: 'left',
                            offset: spaceLeft,
                            axisLabel: {
                                show: true
                            }
                        },
                        {
                            id: 'right',
                            axisLabel: {
                                show: true
                            }
                        }
                    ],
                    series: {
                        barCategoryGap: '20%'
                    }
                }
            }
        ]
    }
})

function seriesByCountry(
    products: Product[],
    countries: Country[],
    importedProducts: Import['products'],
    baseSeries: BarSeriesOption
) {
    return products.map(product => {
        return {
            ...baseSeries,
            itemStyle: {
                borderColor: 'transparent',
                borderWidth: 1,
                color: product?.color
            },
            data: countries.map(country => {
                const importValue = importedProducts.find(
                    ({ product_id, origin_country }) =>
                        origin_country === country.iso &&
                        product_id === product.id
                )

                return {
                    value: importValue?.import_value || undefined,
                    country: country.name,
                    name: product?.name
                }
            })
        } satisfies BarSeriesOption
    })
}

function seriesByProduct(
    products: Product[],
    countries: Country[],
    importedProducts: Import['products'],
    baseSeries: BarSeriesOption
) {
    return countries.map(country => {
        return {
            ...baseSeries,
            itemStyle: {
                borderColor: 'transparent',
                borderWidth: 1
            },
            data: products.map(product => {
                const importValue = importedProducts.find(
                    ({ product_id, origin_country }) =>
                        origin_country === country.iso &&
                        product_id === product.id
                )

                return {
                    value: importValue?.import_value || undefined,
                    country: country.name,
                    name: country?.name,
                    itemStyle: {
                        color: product.color
                    }
                }
            })
        } satisfies BarSeriesOption
    })
}
